<template>
  <div class="mood-tracker">
    <!-- 头部 -->
    <div class="header">
      <h2>📝 每日心情记录</h2>
      <div class="date-info">
        <span class="current-date">{{ currentDate }}</span>
        <span class="weekday">{{ weekday }}</span>
      </div>
    </div>

    <!-- 性别选择 -->
    <div class="gender-section">
      <h3>我是...</h3>
      <div class="gender-options">
        <div
            :class="['gender-option', { selected: user === 1 }]"
            @click="selectGender(1)"
        >
          <!-- 这里使用变量 avatars.WIFE -->
          <img class="gender-icon-img" :src="avatars.WIFE" alt="老婆">
        </div>

        <div
            :class="['gender-option', { selected: user === 2 }]"
            @click="selectGender(2)"
        >
          <!-- 这里使用变量 avatars.HUSBAND -->
          <img class="gender-icon-img" :src="avatars.HUSBAND" alt="老公">
        </div>
      </div>
    </div>

    <!-- 心情选择 -->
    <div class="mood-selector">
      <h3>今天感觉如何？</h3>
      <div class="mood-options">
        <div
            v-for="mood in moodOptions"
            :key="mood.id"
            :class="['mood-option', { selected: selectedMood.id === mood.id }]"
            @click="selectMood(mood)"
        >
          <span class="emoji">{{ mood.emoji }}</span>
          <span class="label">{{ mood.label }}</span>
        </div>
      </div>
    </div>

    <!-- 心情详情 -->
    <div class="mood-details">
      <h3>分享你的感受（可选）</h3>
      <textarea
          v-model="description"
          placeholder="今天发生了什么事？有什么想说的？..."
          rows="4"
          maxlength="500"
          class="description-input"
      ></textarea>
      <div class="char-count">{{ description.length }}/500</div>

      <!-- 标签选择 -->
      <div class="tags-section">
        <h4>选择标签（可选）</h4>
        <div class="tags">
          <span
              v-for="tag in tagOptions"
              :key="tag"
              :class="['tag', { selected: selectedTags.includes(tag) }]"
              @click="toggleTag(tag)"
          >
            {{ tag }}
          </span>
        </div>
      </div>

      <!-- 天气 -->
      <div class="weather-section">
        <h4>天气情况（可选）</h4>
        <select v-model="selectedWeather" class="weather-select">
          <option value="">选择天气</option>
          <option value="sunny">☀️ 晴天</option>
          <option value="cloudy">☁️ 多云</option>
          <option value="rainy">🌧️ 雨天</option>
          <option value="snowy">❄️ 雪天</option>
          <option value="windy">💨 大风</option>
        </select>
      </div>
    </div>

    <!-- 提交按钮 -->
    <div class="actions">
      <button
          @click="saveMood"
          :disabled="!selectedMood.id || !user || isSubmitting"
          class="save-btn"
      >
        {{ isSubmitting ? '保存中...' : '保存今日心情' }}
      </button>
    </div>

    <!-- 历史记录 -->
    <div class="history" v-if="moodHistory.length > 0">
      <h3>📅 近期心情记录</h3>
      <div class="history-list">
        <div
            v-for="record in displayedHistory"
            :key="record.id"
            class="history-item"
        >
          <div class="history-date">
            {{ formatDate(record.recordDate) }}
            <!-- 修正：将 history-user 移出来，作为独立的 Grid 单元格 -->
            <div class="history-user">
              <!-- 原代码: <span class="user-icon" :class="'user-' + record.userId">{{ getUserIcon(record.userId) }}</span> -->

              <!-- 修改为 img 标签，直接绑定 src -->
              <img
                  :src="getUserIcon(record.userId)"
                  alt="User"
                  class="user-icon"
                  :class="'user-' + record.userId"
              >
            </div>
          </div>

          <div class="history-mood">
            <span class="history-emoji">{{ getMoodEmoji(record.moodLevel) }}</span>
            <span class="history-level">{{ getMoodLabel(record.moodLevel) }}</span>
            <!-- 修正：history-meta 独立一行，靠右对齐 -->
            <div class="history-meta">
              <div class="history-tags" v-if="record.tags">
              <span
                  v-for="tag in record.tags.split(',')"
                  :key="tag"
                  class="history-tag"
              >
                {{ tag }}
              </span>
              </div>
              <div class="history-weather" v-if="record.weather">
                {{ getWeatherEmoji(record.weather) }}
              </div>
            </div>
          </div>

          <div class="history-desc" v-if="record.description">
            {{ record.description }}
          </div>
        </div>
      </div>

      <!-- 展开/收起按钮 -->
      <div
          class="toggle-history"
          v-if="moodHistory.length > 3"
          @click="showAllHistory = !showAllHistory"
      >
        <span>{{ showAllHistory ? '收起记录' : '展开全部记录' }}</span>
        <span :class="['arrow-icon', { 'arrow-up': showAllHistory }]">▼</span>
      </div>
    </div>

    <!-- 统计数据 -->
    <div class="stats" v-if="moodStats">
      <h3>📊 本月心情统计</h3>
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-value">{{ moodStats.totalDays || 0 }}</div>
          <div class="stat-label">记录天数</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ moodStats.averageMood?.toFixed(1) || '0.0' }}</div>
          <div class="stat-label">平均心情</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ getMostCommonMood(moodStats.moodDistribution) }}</div>
          <div class="stat-label">最常见心情</div>
        </div>
      </div>

      <!-- 心情趋势图 -->
      <div class="trend-chart" v-if="moodHistory.length > 0">
        <h4>心情趋势</h4>
        <div class="chart-container">
          <div class="chart-bars">
            <div
                v-for="(record, index) in recentHistory"
                :key="index"
                class="chart-bar-container"
            >
              <div class="chart-date">{{ formatChartDate(record.recordDate) }}</div>
              <div class="chart-bar-wrapper">
                <div
                    class="chart-bar"
                    :style="{ height: getMoodHeight(record.moodLevel) + 'px' }"
                    :class="'mood-' + record.moodLevel"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      加载中...
    </div>

    <!-- 成功提示 -->
    <div v-if="showSuccess" class="success-message">
      ✅ 心情记录保存成功！
    </div>

    <!-- 错误提示 -->
    <div v-if="errorMessage" class="error-message">
      ❌ {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { AVATAR_CONFIG } from '@/config.js'

export default {
  name: 'MoodTracker',
  data() {
    return {
      selectedMood: {},
      // 修改：默认选中 1 (老婆/女生)，避免未选择时不显示数据
      user: 1,
      description: '',
      selectedTags: [],
      selectedWeather: '',

      moodOptions: [
        { id: 1, emoji: '😊', label: '开心', level: 5 },
        { id: 2, emoji: '😄', label: '兴奋', level: 4 },
        { id: 3, emoji: '😌', label: '平静', level: 3 },
        { id: 4, emoji: '😕', label: '一般', level: 2 },
        { id: 5, emoji: '😔', label: '低落', level: 1 },
        { id: 6, emoji: '😢', label: '难过', level: 0 }
      ],
      tagOptions: ['工作', '学习', '家庭', '朋友', '健康', '运动', '娱乐', '美食', '旅行'],

      moodHistory: [],
      moodStats: null,

      isSubmitting: false,
      loading: false,
      showSuccess: false,
      errorMessage: '',
      showAllHistory: false,
      avatars: AVATAR_CONFIG,
      axios: this.$http
    }
  },
  computed: {
    currentDate() {
      const now = new Date()
      return now.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    weekday() {
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      return weekdays[new Date().getDay()]
    },
    recentHistory() {
      return this.moodHistory.slice(0, 7)
    },
    displayedHistory() {
      if (this.showAllHistory) {
        return this.moodHistory;
      }
      return this.moodHistory.slice(0, 3);
    }
  },
  mounted() {
    // 预加载头像，优化切换性别时的速度
    this.preloadImages(['../assets/欢欢.jpg', '../assets/我.jpg']);
    this.loadMoodData()
  },
  methods: {
    selectGender(value) {
      if (this.user === value) return;
      this.user = value;
      // 选中性别后立即刷新数据
      this.loadMoodData();
    },

    selectMood(mood) {
      this.selectedMood = mood
    },

    toggleTag(tag) {
      const index = this.selectedTags.indexOf(tag)
      if (index === -1) {
        this.selectedTags.push(tag)
      } else {
        this.selectedTags.splice(index, 1)
      }
    },

    async saveMood() {
      if (!this.selectedMood.id) {
        this.errorMessage = '请选择一个心情'
        return
      }
      if (!this.user) {
        this.errorMessage = '请选择角色'
        return
      }

      this.isSubmitting = true
      this.errorMessage = ''

      try {
        const moodData = {
          moodLevel: this.selectedMood.level,
          userId: this.user,
          description: this.description.trim(),
          tags: this.selectedTags.join(','),
          weather: this.selectedWeather
        }

        const response = await this.axios.post('/api/mood/save', moodData)

        if (response.data.success) {
          this.showSuccess = true
          this.resetForm() // 重置表单，但保留用户选择
          this.loadMoodData()

          setTimeout(() => {
            this.showSuccess = false
          }, 3000)
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.errorMessage = error.response?.data?.message || '保存失败，请重试'
      } finally {
        this.isSubmitting = false
      }
    },

    resetForm() {
      this.selectedMood = {}
      // 不重置 user，让用户保持在当前角色
      // this.user = 1; 
      this.description = ''
      this.selectedTags = []
      this.selectedWeather = ''
    },

    async loadMoodData() {
      this.loading = true
      try {
        const requestData = {
          userId: this.user
        }
        // 模拟 POST 请求传参，如果是 GET 请改回 this.axios.get('/api/mood/history', { params: requestData })
        const [historyRes, statsRes] = await Promise.all([
          this.axios.post('/api/mood/history', requestData),
          this.axios.post('/api/mood/stats', requestData)
        ])

        this.moodHistory = historyRes.data.data || []
        this.moodStats = statsRes.data.data || null
      } catch (error) {
        console.error('加载数据失败:', error)
        this.errorMessage = '加载数据失败'
      } finally {
        this.loading = false
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        month: 'short',
        day: 'numeric'
      })
    },

    formatChartDate(dateString) {
      const date = new Date(dateString)
      return date.getDate() + '日'
    },

    getMoodEmoji(level) {
      const mood = this.moodOptions.find(m => m.level === level)
      return mood ? mood.emoji : '❓'
    },

    getMoodLabel(level) {
      const mood = this.moodOptions.find(m => m.level === level)
      return mood ? mood.label : '未知'
    },

    getUserIcon(userId) {
      // 使用 require 引入图片，让 Webpack 打包时正确处理路径
      if (userId === 1) return this.avatars.WIFE;
      if (userId === 2) return this.avatars.HUSBAND;
      return this.avatars.DEFAULT;
    },

    getWeatherEmoji(weather) {
      const weatherMap = {
        sunny: '☀️',
        cloudy: '☁️',
        rainy: '🌧️',
        snowy: '❄️',
        windy: '💨'
      }
      return weatherMap[weather] || ''
    },

    getMoodHeight(level) {
      return (level + 1) * 10
    },

    getMostCommonMood(distribution) {
      if (!distribution) return '-'
      const entries = Object.entries(distribution)
      if (entries.length === 0) return '-'
      const mostCommon = entries.reduce((a, b) => a[1] > b[1] ? a : b)
      const mood = this.moodOptions.find(m => m.level == mostCommon[0])
      return mood ? mood.label : '-'
    },

    // 新增预加载方法
    preloadImages(imageList) {
      imageList.forEach(src => {
        const img = new Image();
        // 这里的路径 require 是为了确保 webpack 能找到路径
        img.src = require('@/assets/' + src.split('/').pop());
      });
    }
  }
}
</script>

<style scoped>
/* =========================================
   1. 全局盒模型修复
   ========================================= */
.mood-tracker *,
.mood-tracker *::before,
.mood-tracker *::after {
  box-sizing: border-box;
}

.mood-tracker {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background-color: #f7f9fc; /* 增加一个淡淡的背景色，让卡片更突出 */
  min-height: 100vh;
}

/* =========================================
   2. 头部
   ========================================= */
.header {
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.header h2 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.date-info {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: #7f8c8d;
}

.current-date {
  font-size: 18px;
  font-weight: 500;
}

.weekday {
  background: #e8f4fd;
  color: #3498db;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
}

/* =========================================
   3. 通用卡片容器
   ========================================= */
.mood-selector,
.mood-details,
.history,
.stats {
  background: white;
  padding: 25px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 25px;
  max-width: 100%;
  overflow: hidden;
}

/* =========================================
   4. 心情选择
   ========================================= */
.mood-selector h3 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.mood-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.mood-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 15px;
  border: 2px solid #f0f0f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.mood-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  border-color: #bce0fd;
}

.mood-option.selected {
  border-color: #3498db;
  background: #f0f9ff;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(52, 152, 219, 0.2);
}

.emoji {
  font-size: 36px;
  margin-bottom: 8px;
}

.label {
  font-size: 15px;
  font-weight: 500;
  color: #2c3e50;
}

/* =========================================
   5. 心情详情与表单 - 性别选择（无文字版 - 修复修复）
   ========================================= */
.gender-section {
  margin-bottom: 30px;
  padding-bottom: 25px;
  border-bottom: 1px dashed #f0f0f0;
}

.gender-section h3 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
  font-size: 18px;
}

.gender-options {
  display: flex;
  justify-content: center;
  gap: 30px; /*稍微调小一点，适应手机屏幕 */
}

/* 性别选项容器：强制正圆 */
.gender-option {
  /* 布局与尺寸 */
  width: 70px;
  height: 70px;

  /* 关键修复：禁止 flex 收缩，防止在手机窄屏上被挤压变形 */
  flex-shrink: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  /* 样式与交互 */
  border-radius: 50%; /* 强制圆形 */
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1.275);

  /* 边框与阴影 (默认未选中状态) */
  border: 3px solid transparent;
  background: #fff;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
  position: relative;

  /* 确保内容不会溢出 */
  overflow: hidden;
}

/* 选中状态 */
.gender-option.selected {
  border-color: #3498db;
  transform: scale(1.1); /* 手机上稍微放大一点点，别太大 */
  box-shadow: 0 8px 20px rgba(52, 152, 219, 0.3);
}

/* 头像图片样式 */
.gender-icon-img {
  width: 100%;
  height: 100%;

  /* 强制正圆 */
  border-radius: 50%;

  /* 关键：在任何宽高比下都填满并裁剪，不拉伸，不变形 */
  object-fit: cover;

  border: none;
  display: block;
  transition: all 0.3s;

  /* 额外保险：设置最小宽高，防止在某些渲染模式下塌陷 */
  min-width: 100%;
  min-height: 100%;
}

/* 鼠标悬停效果 */
.gender-option:hover {
  transform: scale(1.05);
  border-color: #bce0fd;
}

.mood-details h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 18px;
}

.description-input {
  width: 100%;
  box-sizing: border-box;
  padding: 15px;
  border: 2px solid #f0f0f0;
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  background: #fafafa;
}

.description-input:focus {
  outline: none;
  border-color: #3498db;
  background: white;
}

.char-count {
  text-align: right;
  color: #95a5a6;
  font-size: 13px;
  margin-top: 8px;
}

.tags-section, .weather-section {
  margin-top: 25px;
}

.tags-section h4, .weather-section h4 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 16px;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag {
  padding: 8px 20px;
  background: #f5f7fa;
  border: 1px solid #e1e4e8;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
  color: #586069;
}

.tag:hover {
  background: #e1e4e8;
  color: #2c3e50;
}

.tag.selected {
  background: #3498db;
  color: white;
  border-color: #3498db;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
}

.weather-select {
  padding: 12px 15px;
  border: 2px solid #f0f0f0;
  border-radius: 10px;
  font-size: 16px;
  width: 100%;
  max-width: 320px;
  background: white;
  box-sizing: border-box;
  color: #555;
}

/* =========================================
   6. 按钮
   ========================================= */
.actions {
  text-align: center;
  margin: 40px 0;
}

.save-btn {
  padding: 16px 50px;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(52, 152, 219, 0.4);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* =========================================
   7. 历史记录
   ========================================= */
.history h3, .stats h3 {
  color: #2c3e50;
  margin-bottom: 25px;
  text-align: center;
  font-size: 20px;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.history-item {
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 12px;
  background: #fff;
  /* Grid 布局：日期 | 图标 | 心情 | Meta | 描述 */
  grid-template-columns: 70px 50px minmax(140px, 1fr) auto;
  gap: 15px;
  align-items: center;
  transition: box-shadow 0.3s;
}

.history-item:hover {
  box-shadow: 0 4px 15px rgba(0,0,0,0.06);
  border-color: #e1e4e8;
}

.history-date {
  font-weight: 600;
  color: #2c3e50;
  font-size: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.history-user {
  display: flex;
  justify-content: center;
  align-items: center;
}

.user-icon {
  /* 基础尺寸 */
  width: 38px;
  height: 38px;

  /* 核心样式：圆形 + 裁剪 */
  border-radius: 50%;
  object-fit: cover;

  /* 居中显示 */
  display: block;

  /* 默认白色边框，增加层次感 */
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  /* 确保图片作为块级元素没有空隙 */
  vertical-align: middle;
}

/* 性别边框颜色 (只改边框，不改背景) */
.user-1.user-icon {
  border-color: #db2777; /* 老婆粉色边框 */
}

.user-2.user-icon {
  border-color: #0284c7; /* 老公蓝色边框 */
}

/* 性别色彩 */
.user-1 { background: #fce7f3; color: #db2777; }
.user-2 { background: #e0f2fe; color: #0284c7; }

.history-mood {
  display: flex;
  align-items: center;
  gap: 10px;
}

.history-emoji {
  font-size: 28px;
}

.history-level {
  font-size: 15px;
  color: #555;
  font-weight: 500;
  white-space: nowrap; /* 防止心情文字换行 */
}

.history-desc {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  grid-column: 3 / -1; /* 描述从心情列开始，横跨到最后 */
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px dashed #f0f0f0;
  width: 100%;
}

.history-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto; /* 关键：在 flex 容器中把内容推到右边（如果不用 grid align） */
  justify-self: end; /* 栅格对齐靠右 */
}

.history-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.history-tag {
  padding: 4px 10px;
  background: #edf2f7;
  color: #4a5568;
  border-radius: 15px;
  font-size: 12px;
  white-space: nowrap;
}

.history-weather {
  font-size: 22px;
}

.toggle-history {
  text-align: center;
  margin-top: 25px;
  padding-top: 15px;
  border-top: 1px dashed #ddd;
  color: #3498db;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: color 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
}

.toggle-history:hover {
  color: #2980b9;
}

.arrow-icon {
  font-size: 12px;
  display: inline-block;
  transition: transform 0.3s ease;
}

.arrow-icon.arrow-up {
  transform: rotate(180deg);
}

/* =========================================
   8. 统计数据
   ========================================= */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: #f8fafd;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #3498db;
  margin-bottom: 8px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
}

/* =========================================
   9. 趋势图
   ========================================= */
.trend-chart {
  margin-top: 30px;
}

.trend-chart h4 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.chart-container {
  padding: 20px;
  background: #f8fafd;
  border-radius: 12px;
}

.chart-bars {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 200px;
}

.chart-bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.chart-date {
  font-size: 12px;
  color: #95a5a6;
  margin-bottom: 8px;
}

.chart-bar-wrapper {
  height: 150px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
}

.chart-bar {
  width: 60%;
  max-width: 25px;
  border-radius: 6px 6px 0 0;
  background: #3498db;
  transition: height 0.5s ease;
}

.chart-bar.mood-5 { background: #2ecc71; }
.chart-bar.mood-4 { background: #3498db; }
.chart-bar.mood-3 { background: #f1c40f; }
.chart-bar.mood-2 { background: #e67e22; }
.chart-bar.mood-1 { background: #e74c3c; }
.chart-bar.mood-0 { background: #c0392b; }

/* =========================================
   10. 状态与提示
   ========================================= */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px;
  color: #95a5a6;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-message {
  position: fixed;
  top: 20px;
  right: 20px;
  background: #2ecc71;
  color: white;
  padding: 15px 25px;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(46, 204, 113, 0.4);
  animation: slideIn 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
}

.error-message {
  background: #e74c3c;
  color: white;
  padding: 15px;
  border-radius: 10px;
  margin: 20px 0;
  text-align: center;
  animation: fadeIn 0.3s ease;
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.2);
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* =========================================
   11. 移动端响应式适配
   ========================================= */
@media (max-width: 768px) {
  .mood-tracker {
    padding: 15px;
  }

  .mood-options {
    grid-template-columns: repeat(2, 1fr);
  }

  /* 移动端 Grid 调整：图标 | 日期+心情 | Meta | 描述 */
  .history-item {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto auto auto;
    gap: 10px;
    padding: 15px;
  }

  /* 1. 用户图标：左上角 */
  .history-user {
    grid-column: 1;
    grid-row: 1;
  }

  /* 2. 日期：图标旁边 */
  .history-date {
    grid-column: 2;
    grid-row: 1;
    align-self: center;
    flex-direction: row;
    justify-content: flex-start;
    gap: 10px;
    font-size: 16px;
  }

  /* 3. 心情：第二行，占满 */
  .history-mood {
    grid-column: 1 / -1;
    grid-row: 2;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  /* 4. Meta：第三行 */
  .history-meta {
    grid-column: 1 / -1;
    grid-row: 3;
    justify-content: flex-start;
  }

  /* 5. 描述：第四行 */
  .history-desc {
    grid-column: 1 / -1;
    grid-row: 4;
    border-top: none;
    padding-top: 0;
    margin-top: 0;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
}

@media (max-width: 480px) {
  .header h2 {
    font-size: 1.5rem;
  }

  /* ----------------------------------------------------------------- */
  /* ⚠️ 重点修改/删除下面这一段 ⚠️ */
  /* ----------------------------------------------------------------- */

  .gender-options {
    gap: 15px; /* 只保留间距调整，或者也可以删掉这段用上面的默认样式 */
  }

  /*
     ⛔ 删除这段代码！
     .gender-option {
       padding: 10px 20px;
       font-size: 14px;
     }
  */
  /* 删除原因是：上面已经定义了 .gender-option 为 70px * 70px 正方形，
     这里的 padding 会破坏正方形结构导致变成椭圆。
  */
}
</style>